import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { notifications } from '@mantine/notifications';
import { supabase, User, Favorite, UserPreferences } from '../lib/supabase';
import axios from 'axios';

interface AuthContextType {
  user: User | null;
  favorites: Favorite[];
  userPreferences: UserPreferences | null;
  loading: boolean;
  isPro: boolean;
  proExpiresAt: string | null;
  sendOTP: (email: string) => Promise<void>;
  verifyOTP: (email: string, token: string) => Promise<void>;
  logout: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  addFavorite: (name: string, link: string, playerData?: { club?: string; elo?: number; classification?: string; license_number?: string }) => Promise<void>;
  removeFavorite: (link: string) => Promise<void>;
  isFavorite: (link: string) => boolean;
  updateFavoriteUrl: (oldLink: string, newLink: string) => Promise<void>;
  refreshProStatus: () => Promise<void>;
  setMyProfile: (playerLink: string, playerData: { name: string; club?: string; elo?: number; classification?: string; license_number?: string; }, avatar?: string) => Promise<void>;
  setProfileAvatar: (avatar: string) => Promise<void>;
}

const notificationTranslations = {
  en: {
    signedOut: {
      title: 'Signed Out',
      message: 'You have been successfully signed out.',
    },
    addedToFavorites: {
      title: 'Added to Favorites',
      message: (name: string) => `${name} has been added to your favorites.`,
    },
    alreadyFavorite: 'Already in your favorites',
    removedFromFavorites: {
      title: 'Removed from Favorites',
      message: (name: string) => `${name} has been removed from your favorites.`,
    },
    accountDeleted: {
      title: 'Account Deleted',
      message: 'Your account has been permanently deleted.',
    },
  },
  fr: {
    signedOut: {
      title: 'Déconnecté',
      message: 'Tu as été déconnecté avec succès.',
    },
    addedToFavorites: {
      title: 'Ajouté aux favoris',
      message: (name: string) => `${name} a été ajouté à tes favoris.`,
    },
    alreadyFavorite: 'Déjà dans tes favoris',
    removedFromFavorites: {
      title: 'Retiré des favoris',
      message: (name: string) => `${name} a été retiré de tes favoris.`,
    },
    accountDeleted: {
      title: 'Compte supprimé',
      message: 'Ton compte a été définitivement supprimé.',
    },
  },
  de: {
    signedOut: {
      title: 'Abgemeldet',
      message: 'Du wurdest erfolgreich abgemeldet.',
    },
    addedToFavorites: {
      title: 'Zu Favoriten hinzugefügt',
      message: (name: string) => `${name} wurde zu deinen Favoriten hinzugefügt.`,
    },
    alreadyFavorite: 'Bereits in deinen Favoriten',
    removedFromFavorites: {
      title: 'Aus Favoriten entfernt',
      message: (name: string) => `${name} wurde aus deinen Favoriten entfernt.`,
    },
    accountDeleted: {
      title: 'Konto gelöscht',
      message: 'Dein Konto wurde dauerhaft gelöscht.',
    },
  },
  it: {
    signedOut: {
      title: 'Disconnesso',
      message: 'Sei stato disconnesso con successo.',
    },
    addedToFavorites: {
      title: 'Aggiunto ai preferiti',
      message: (name: string) => `${name} è stato aggiunto ai tuoi preferiti.`,
    },
    alreadyFavorite: 'Già nei tuoi preferiti',
    removedFromFavorites: {
      title: 'Rimosso dai preferiti',
      message: (name: string) => `${name} è stato rimosso dai tuoi preferiti.`,
    },
    accountDeleted: {
      title: 'Account eliminato',
      message: 'Il tuo account è stato eliminato definitivamente.',
    },
  },
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Helper to get current language from URL or localStorage
const getCurrentLanguage = (): 'en' | 'fr' | 'de' | 'it' => {
  const path = window.location.pathname;
  const match = path.match(/^\/(en|fr|de|it)/);
  if (match) {
    return match[1] as 'en' | 'fr' | 'de' | 'it';
  }
  return 'en';
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [userPreferences, setUserPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPro, setIsPro] = useState(false);
  const [proExpiresAt, setProExpiresAt] = useState<string | null>(null);
  const [lastProCheck, setLastProCheck] = useState<number>(0);

  // Check for existing session on mount
  useEffect(() => {
    checkUser();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        const user: User = {
          id: session.user.id,
          email: session.user.email!,
          created_at: session.user.created_at
        };
        setUser(user);
        loadFavorites(session.user.id);
        loadUserPreferences(session.user.id);
        checkProStatus(session.user.id);
      } else {
        setUser(null);
        setFavorites([]);
        setUserPreferences(null);
        setIsPro(false);
        setProExpiresAt(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkUser = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.user) {
        const user: User = {
          id: session.user.id,
          email: session.user.email!,
          created_at: session.user.created_at
        };
        setUser(user);
        await loadFavorites(session.user.id);
        await loadUserPreferences(session.user.id);
        await checkProStatus(session.user.id);
      }
    } catch (error) {
      console.error('Error checking user:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkProStatus = async (userId: string) => {
    // Cache Pro status for 5 minutes to avoid duplicate calls
    const now = Date.now();
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

    if (now - lastProCheck < CACHE_DURATION) {
      console.log('⏭️ Skipping Pro status check (cached)');
      return;
    }

    try {
      console.log('🔍 Checking Pro status for user:', userId);
      setLastProCheck(now);

      // Call RevenueCat API directly with public API key
      const REVENUECAT_PUBLIC_KEY = 'appl_BLSiwXSPdDpBxEeKNQSoDtJYWKS';
      const response = await axios.get(
        `https://api.revenuecat.com/v1/subscribers/${userId}`,
        {
          headers: {
            'Authorization': `Bearer ${REVENUECAT_PUBLIC_KEY}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('📦 RevenueCat response:', response.data);

      // Check for "TTStats Pro" entitlement (note: it's case-sensitive!)
      const entitlements = response.data.subscriber?.entitlements || {};
      console.log('🎟️ Entitlements:', entitlements);

      const proEntitlement = entitlements['TTStats Pro'];
      console.log('👑 Pro entitlement:', proEntitlement);

      const isPro = proEntitlement && new Date(proEntitlement.expires_date) > new Date();
      const expiresAt = proEntitlement?.expires_date || null;

      console.log('✅ Pro status received:', { isPro, expiresAt });

      setIsPro(!!isPro);
      setProExpiresAt(expiresAt);
    } catch (error) {
      console.error('❌ Error checking Pro status:', error);
      console.log('ℹ️ Defaulting to free user');
      setIsPro(false);
      setProExpiresAt(null);
    }
  };

  const refreshProStatus = async () => {
    if (user) {
      await checkProStatus(user.id);
    }
  };

  const loadFavorites = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('favorites')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (data && !error) {
        setFavorites(data);
      }
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };

  const sendOTP = async (email: string) => {
    const normalizedEmail = email.trim().toLowerCase();

    // Basic email validation
    if (!normalizedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      throw new Error('Please enter a valid email address');
    }

    try {
      // Send OTP code via email
      const { error } = await supabase.auth.signInWithOtp({
        email: normalizedEmail,
        options: {
          shouldCreateUser: true,
        }
      });

      if (error) throw error;
    } catch (error) {
      console.error('Error sending OTP:', error);
      throw error;
    }
  };

  const verifyOTP = async (email: string, token: string) => {
    const normalizedEmail = email.trim().toLowerCase();

    try {
      const { error } = await supabase.auth.verifyOtp({
        email: normalizedEmail,
        token: token,
        type: 'email'
      });

      if (error) throw error;
    } catch (error) {
      console.error('Error verifying OTP:', error);
      throw error;
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setFavorites([]);
    setIsPro(false);
    setProExpiresAt(null);

    const lang = getCurrentLanguage();
    const t = notificationTranslations[lang];

    notifications.show({
      title: t.signedOut.title,
      message: t.signedOut.message,
      color: 'blue',
      autoClose: 3000,
    });
  };

  const deleteAccount = async () => {
    if (!user) throw new Error('Not authenticated');

    try {
      // Delete all favorites (all personal data)
      await supabase
        .from('favorites')
        .delete()
        .eq('user_id', user.id);

      // TODO: Call backend API endpoint to delete user from Supabase Auth
      // This requires service role key which can't be used from client side
      // For now, we delete all personal data and sign out
      // The auth record will be purged manually or via backend endpoint

      // Optionally call your backend API:
      // await fetch('https://apiv2.ttstats.ch/delete-user', {
      //   method: 'DELETE',
      //   headers: { 'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}` }
      // });

      // Sign out and clear state
      await supabase.auth.signOut();
      setUser(null);
      setFavorites([]);

      const lang = getCurrentLanguage();
      const t = notificationTranslations[lang];

      notifications.show({
        title: t.accountDeleted.title,
        message: t.accountDeleted.message,
        color: 'red',
        autoClose: 5000,
      });
    } catch (error) {
      console.error('Error deleting account:', error);
      throw error;
    }
  };

  const addFavorite = async (name: string, link: string, playerData?: { club?: string; elo?: number; classification?: string; license_number?: string }) => {
    if (!user) throw new Error('Not authenticated');

    try {
      const favoriteData: any = {
        user_id: user.id,
        name,
        link,
        ...(playerData?.club !== undefined && { club: playerData.club }),
        ...(playerData?.elo !== undefined && { elo: playerData.elo }),
        ...(playerData?.classification !== undefined && { classification: playerData.classification }),
        ...(playerData?.license_number !== undefined && { license_number: playerData.license_number })
      };

      const { data, error } = await supabase
        .from('favorites')
        .insert([favoriteData])
        .select()
        .single();

      if (error) {
        // If it's a duplicate, just ignore
        if (error.code === '23505') {
          const lang = getCurrentLanguage();
          const t = notificationTranslations[lang];

          notifications.show({
            message: t.alreadyFavorite,
            color: 'yellow',
            autoClose: 2000,
          });
          return;
        }
        throw error;
      }

      if (data) {
        setFavorites(prev => [data, ...prev]);

        const lang = getCurrentLanguage();
        const t = notificationTranslations[lang];

        notifications.show({
          title: t.addedToFavorites.title,
          message: t.addedToFavorites.message(name),
          color: 'green',
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error('Error adding favorite:', error);
      throw error;
    }
  };

  const removeFavorite = async (link: string) => {
    if (!user) throw new Error('Not authenticated');

    try {
      // Get the favorite name before removing
      const favorite = favorites.find(fav => fav.link === link);

      const { error } = await supabase
        .from('favorites')
        .delete()
        .eq('user_id', user.id)
        .eq('link', link);

      if (error) throw error;

      setFavorites(prev => prev.filter(fav => fav.link !== link));

      const lang = getCurrentLanguage();
      const t = notificationTranslations[lang];

      notifications.show({
        title: t.removedFromFavorites.title,
        message: favorite ? t.removedFromFavorites.message(favorite.name) : t.removedFromFavorites.title,
        color: 'red',
        autoClose: 3000,
      });
    } catch (error) {
      console.error('Error removing favorite:', error);
      throw error;
    }
  };

  const isFavorite = (link: string): boolean => {
    return favorites.some(fav => fav.link === link);
  };

  const loadUserPreferences = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (data && !error) {
        setUserPreferences(data);
      }
    } catch (error) {
      console.error('Error loading user preferences:', error);
    }
  };

  const setMyProfile = async (
    playerLink: string,
    playerData: {
      name: string;
      club?: string;
      elo?: number;
      classification?: string;
      license_number?: string;
    },
    avatar?: string
  ) => {
    if (!user) throw new Error('Not authenticated');

    try {
      const preferences: Partial<UserPreferences> = {
        user_id: user.id,
        my_player_link: playerLink,
        my_player_name: playerData.name,
        my_player_club: playerData.club,
        my_player_elo: playerData.elo,
        my_player_classification: playerData.classification,
        my_player_license_number: playerData.license_number,
        ...(avatar && { profile_avatar: avatar }),
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from('user_preferences')
        .upsert(preferences, { onConflict: 'user_id' })
        .select()
        .single();

      if (error) throw error;

      if (data) {
        setUserPreferences(data);

        const lang = getCurrentLanguage();
        notifications.show({
          title: lang === 'en' ? 'Profile Set' : lang === 'fr' ? 'Profil défini' : lang === 'de' ? 'Profil festgelegt' : 'Profilo impostato',
          message: lang === 'en' ? 'Your profile has been updated' : lang === 'fr' ? 'Ton profil a été mis à jour' : lang === 'de' ? 'Dein Profil wurde aktualisiert' : 'Il tuo profilo è stato aggiornato',
          color: 'green',
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error('Error setting my profile:', error);
      throw error;
    }
  };

  const setProfileAvatar = async (avatar: string) => {
    if (!user) throw new Error('Not authenticated');

    try {
      const preferences: Partial<UserPreferences> = {
        user_id: user.id,
        profile_avatar: avatar,
        updated_at: new Date().toISOString(),
        // Keep existing my_player_link if it exists
        ...(userPreferences?.my_player_link && {
          my_player_link: userPreferences.my_player_link,
        }),
      };

      const { data, error } = await supabase
        .from('user_preferences')
        .upsert(preferences, { onConflict: 'user_id' })
        .select()
        .single();

      if (error) throw error;

      if (data) {
        setUserPreferences(data);
      }
    } catch (error) {
      console.error('Error setting profile avatar:', error);
      throw error;
    }
  };

  const updateFavoriteUrl = async (oldLink: string, newLink: string) => {
    if (!user) throw new Error('Not authenticated');

    try {
      const { data, error } = await supabase
        .from('favorites')
        .update({ link: newLink })
        .eq('user_id', user.id)
        .eq('link', oldLink)
        .select()
        .single();

      if (error) throw error;

      if (data) {
        // Update local state
        setFavorites(prev =>
          prev.map(fav => fav.link === oldLink ? data : fav)
        );

        console.log(`✅ Updated favorite URL: ${oldLink} → ${newLink}`);
      }
    } catch (error) {
      console.error('Error updating favorite URL:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    favorites,
    userPreferences,
    loading,
    isPro,
    proExpiresAt,
    sendOTP,
    verifyOTP,
    logout,
    deleteAccount,
    addFavorite,
    removeFavorite,
    isFavorite,
    updateFavoriteUrl,
    refreshProStatus,
    setMyProfile,
    setProfileAvatar,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
